import type { MedplumClient } from '@medplum/core'
import { logError } from './errorUtils'

/** Give up on the logo fetch after this long so it can never stall PDF generation. */
const LOGO_FETCH_TIMEOUT_MS = 8000

/**
 * Fetches a facility logo as a data URL for embedding in a generated PDF.
 *
 * Robust by design — a logo is non-critical, so any problem degrades to "no logo"
 * (returns `undefined`) rather than blocking the order PDF:
 *  - Resolves the `Binary/{id}` reference against the frontend's own medplum client
 *    (browser-reachable host). The middleware may build `logoUrl` with a private host
 *    (e.g. `api-private.*`) that the browser cannot reach.
 *  - Times out the download AND aborts it (via AbortSignal) so an unreachable host
 *    neither stalls generation nor leaks an open connection until the OS TCP timeout.
 *  - Verifies the result is actually an image — `medplum.download()` returns the error
 *    body (e.g. an OperationOutcome) as a blob on a non-2xx response instead of throwing,
 *    and a non-image data URL makes pdfmake hang.
 */
export async function loadLogoDataUrl(
  medplum: MedplumClient,
  logoUrl: string,
): Promise<string | undefined> {
  try {
    const ref = logoUrl.match(/Binary\/[^/?#]+/)?.[0] ?? logoUrl
    const blob = await downloadWithTimeout(medplum, ref, LOGO_FETCH_TIMEOUT_MS)

    if (!blob.type.startsWith('image/')) {
      logError(
        'Facility logo fetch',
        new Error(`Expected an image, received "${blob.type || 'unknown'}"`),
      )
      return undefined
    }

    return await blobToDataUrl(blob)
  } catch (err) {
    logError('Facility logo fetch', err)
    return undefined
  }
}

/**
 * Downloads a Binary, aborting the underlying request (not just the wrapper promise)
 * if it exceeds `ms`. The reject guarantees the caller settles even if the abort
 * isn't honored; the abort frees the browser connection slot.
 */
function downloadWithTimeout(medplum: MedplumClient, ref: string, ms: number): Promise<Blob> {
  const controller = new AbortController()
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      controller.abort()
      reject(new Error(`Logo fetch timed out after ${ms}ms`))
    }, ms)
  })
  return Promise.race([medplum.download(ref, { signal: controller.signal }), timeout]).finally(() =>
    clearTimeout(timeoutId),
  )
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result)
      else reject(new Error('FileReader did not return a string'))
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}
